import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { NumericFilterInput } from "../Filters/NumericFilterInput";
import { SearchSelectInfiniteScroll } from "../SearchSelectInfiniteScroll/SearchSelectInfiniteScroll";
import { DeleteIcon, XIcon } from "../Icons/Icons";
import {
  TextSmRegular,
  TextSmSemibold,
  TextXsRegular,
} from "../Typography/TypographyV2";
import { useStoreState } from "../../util/util";
import type {
  AdvancedFilterCard,
  AdvancedFilterCollectionInstance,
  AdvancedFilterOtherFilter,
  AdvancedFilterQuantitativeFilter,
  NumericFilterCriterion,
  OptionType,
} from "../../types/types";
import {
  numericFilterCriterionToQuantitativeCriterion,
  quantitativeCriterionToNumericFilterCriterion,
  useAdvancedFilterOtherFilterChoices,
} from "./advancedFiltersUtils";

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
  padding: ${({ theme }) => theme.resolve("spacing_lg")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  background: ${({ theme }) => theme.resolve("color_bg_surface_standard")};
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
`;

const CardHeaderLabels = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xxs")};
  min-width: 0;
`;

const PlainIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
`;

const RemoveCardButton = styled(PlainIconButton)`
  padding: ${({ theme }) => theme.resolve("spacing_xs")};
  margin: -${({ theme }) => theme.resolve("spacing_xs")};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  &:hover {
    background: ${({ theme }) => theme.resolve("color_bg_surface_subtle")};
  }
`;

const ConditionRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  align-items: start;
`;

// Centers the label against the 30px-tall control's first line.
const ConditionLabel = styled.div`
  padding-top: ${({ theme }) => theme.resolve("spacing_xs")};
`;

/**
 * `CreatableSelectBox` (under `SearchSelectInfiniteScroll`) hardcodes a 50px
 * floating-label control with no style override prop — shrink it to the
 * 30px height of the numeric inputs per the Figma spec.
 */
const CompactSelectWrapper = styled.div`
  .select__control {
    height: auto;
    min-height: 30px;
  }
  .select__value-container {
    margin-top: 0;
    padding: 0 ${({ theme }) => theme.resolve("spacing_md")};
  }
  .select__value-container > div {
    margin-top: 0;
  }
  .select__placeholder {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    margin: 0;
    font-size: ${({ theme }) => theme.resolve("font_size_md")};
  }
  .select__indicator {
    padding: ${({ theme }) => theme.resolve("spacing_xs")}
      ${({ theme }) => theme.resolve("spacing_md")};
  }
`;

const SelectedValues = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  margin-top: ${({ theme }) => theme.resolve("spacing_xs")};
`;

const SelectedValueChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  padding: ${({ theme }) => theme.resolve("spacing_xxs")}
    ${({ theme }) => theme.resolve("spacing_sm")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
`;

type AdvancedFiltersCriterionCardProps = {
  instance: AdvancedFilterCollectionInstance;
  card: AdvancedFilterCard;
  onChange: (next: AdvancedFilterCard) => void;
  onRemove: () => void;
  onQuantitativeInvalidChange: (filterId: string, isInvalid: boolean) => void;
};

function QuantitativeConditionRow({
  filter,
  card,
  onChange,
  onInvalidChange,
}: {
  filter: AdvancedFilterQuantitativeFilter;
  card: AdvancedFilterCard;
  onChange: (next: AdvancedFilterCard) => void;
  onInvalidChange: (isInvalid: boolean) => void;
}) {
  const existing = card.quantitative.find(
    (c) => c.quantitative_filter_id === filter.id
  );
  const criterion = quantitativeCriterionToNumericFilterCriterion(existing);

  const applyCriterion = (next: NumericFilterCriterion) => {
    const nextCriterion = numericFilterCriterionToQuantitativeCriterion(
      filter.id,
      next
    );
    onChange({
      ...card,
      quantitative: [
        ...card.quantitative.filter(
          (c) => c.quantitative_filter_id !== filter.id
        ),
        nextCriterion,
      ],
    });
  };

  const clearCriterion = () => {
    onChange({
      ...card,
      quantitative: card.quantitative.filter(
        (c) => c.quantitative_filter_id !== filter.id
      ),
    });
  };

  return (
    <ConditionRow>
      <ConditionLabel>
        <TextSmRegular>{filter.display_name}</TextSmRegular>
      </ConditionLabel>
      <NumericFilterInput
        inline
        filterType={`${card.collection_instance_id}-${card.key_attribute_value}-${filter.id}`}
        operators={filter.operators}
        initialCriterion={criterion}
        onApply={applyCriterion}
        onClear={clearCriterion}
        defaultOperator={"between"}
        onInvalidChange={onInvalidChange}
      />
    </ConditionRow>
  );
}

/**
 * One other-filter condition row. Value options come from the instance's own
 * choices endpoint — the list items products actually record in the
 * configured collection, narrowed to this card's Key Attribute value — so
 * the dropdown can't offer a value the submitted card would then match
 * nothing with.
 */
function OtherConditionRow({
  instance,
  filter,
  card,
  onChange,
}: {
  instance: AdvancedFilterCollectionInstance;
  filter: AdvancedFilterOtherFilter;
  card: AdvancedFilterCard;
  onChange: (next: AdvancedFilterCard) => void;
}) {
  const { t } = useTranslation();
  const { storefront_id } = useStoreState();

  const {
    choices,
    isLoading,
    onMenuOpen,
    onMenuScrollToBottom,
    onInputChange,
  } = useAdvancedFilterOtherFilterChoices({
    storefrontId: storefront_id,
    instanceId: instance.id,
    otherFilterId: filter.id,
    keyAttributeValue: card.key_attribute_value,
  });

  const options: OptionType<string>[] = useMemo(
    () => choices.map((choice) => ({ value: choice, label: choice })),
    [choices]
  );

  const existing = card.other.find((c) => c.other_filter_id === filter.id);
  const selectedValues = existing?.values ?? [];
  const selectedOptions: OptionType<string>[] = selectedValues.map((value) => ({
    value,
    label: value,
  }));

  const setValues = (values: string[]) => {
    const otherWithoutThisFilter = card.other.filter(
      (c) => c.other_filter_id !== filter.id
    );
    onChange({
      ...card,
      other:
        values.length > 0
          ? [...otherWithoutThisFilter, { other_filter_id: filter.id, values }]
          : otherWithoutThisFilter,
    });
  };

  const handleChange = (next: OptionType<string>[] | null) =>
    setValues((next ?? []).map((option) => option.value));

  const handleRemoveValue = (value: string) =>
    setValues(selectedValues.filter((v) => v !== value));

  return (
    <ConditionRow>
      <ConditionLabel>
        <TextSmRegular>{filter.attribute_display_name}</TextSmRegular>
      </ConditionLabel>
      <div>
        <CompactSelectWrapper>
          <SearchSelectInfiniteScroll
            baseUrl={null}
            getOptions={() => options}
            options={options}
            isMulti
            normalPlaceholder
            isLoading={isLoading}
            value={selectedOptions}
            onChange={handleChange}
            onMenuOpen={onMenuOpen}
            onMenuScrollToBottom={onMenuScrollToBottom}
            onInputChange={onInputChange}
            placeholder={t("Select value(s)")}
            name={`${filter.id}-other-filter`}
            // This card isn't a react-hook-form form (edits are batch-applied via
            // local state, see AdvancedFiltersModal's doc comment) — CreatableSelectBox
            // requires formState/errors for its error-styling, so stub them the same
            // way other non-RHF callers do (e.g. CreatePimProductFromUpload.tsx).
            errors={{}}
            formState={{ submitCount: 0 }}
          />
        </CompactSelectWrapper>
        {selectedValues.length > 0 && (
          <SelectedValues>
            {selectedValues.map((value) => (
              <SelectedValueChip key={value}>
                <TextXsRegular>{value}</TextXsRegular>
                <PlainIconButton
                  type="button"
                  aria-label={t("Remove")}
                  data-testid={`remove-advanced-filter-value-${filter.id}-${value}`}
                  onClick={() => handleRemoveValue(value)}
                >
                  <XIcon width={12} height={12} />
                </PlainIconButton>
              </SelectedValueChip>
            ))}
          </SelectedValues>
        )}
      </div>
    </ConditionRow>
  );
}

export const AdvancedFiltersCriterionCard = ({
  instance,
  card,
  onChange,
  onRemove,
  onQuantitativeInvalidChange,
}: AdvancedFiltersCriterionCardProps) => {
  const { t } = useTranslation();
  const validQuantitativeFilters = instance.quantitative_filters.filter(
    (filter) => filter.is_valid
  );
  const validOtherFilters = instance.other_filters.filter(
    (filter) => filter.is_valid
  );

  return (
    <CardWrapper
      data-testid={`advanced-filters-card-${card.collection_instance_id}-${card.key_attribute_value}`}
    >
      <CardHeader>
        <CardHeaderLabels>
          <TextSmSemibold>{instance.collection_name}</TextSmSemibold>
          <TextSmRegular color="secondary">
            {instance.key_attribute_display_name}: {card.key_attribute_value}
          </TextSmRegular>
        </CardHeaderLabels>
        <RemoveCardButton
          type="button"
          aria-label={t("Remove filter")}
          data-testid={`remove-advanced-filter-card-${card.collection_instance_id}-${card.key_attribute_value}`}
          onClick={onRemove}
        >
          <DeleteIcon width={16} height={16} />
        </RemoveCardButton>
      </CardHeader>

      {validQuantitativeFilters.map((filter) => (
        <QuantitativeConditionRow
          key={filter.id}
          filter={filter}
          card={card}
          onChange={onChange}
          onInvalidChange={(isInvalid) =>
            onQuantitativeInvalidChange(filter.id, isInvalid)
          }
        />
      ))}

      {validOtherFilters.map((filter) => (
        <OtherConditionRow
          key={filter.id}
          instance={instance}
          filter={filter}
          card={card}
          onChange={onChange}
        />
      ))}
    </CardWrapper>
  );
};

AdvancedFiltersCriterionCard.displayName = "AdvancedFiltersCriterionCard";
